import { html, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { highlightCode } from "@/feedback/code-highlight";
import { escapeHtml } from "@/utilities";
import "@/components/copy_button";

/**
 * Renders a read-only, syntax-highlighted code listing with a copy button.
 *
 * The source code is provided in a child <pre>, which also serves as a no-JS
 * fallback. Unlike d-feedback-code-tab this carries no annotation
 * machinery, so it suits standalone code such as the sample solutions on the
 * activity info page. Highlighting happens client-side and upgrades the
 * plain-escaped lines once it resolves. The markup matches the .code-listing
 * styles in app/assets/stylesheets/components/code_listing.css.scss.
 *
 * @element d-code-viewer
 *
 * @prop {string} programmingLanguage - Language used for syntax highlighting.
 */
@customElement("d-code-viewer")
export class CodeViewer extends DodonaElement {
    @property({ type: String, attribute: "programming-language" }) programmingLanguage = "";

    @state() private _lines: string[] = [];
    private _code = "";
    // Identifies the latest highlight request so stale async results can be discarded.
    private _highlightRequestKey = "";

    override willUpdate(changedProperties: PropertyValues): void {
        const firstRender = !this.hasUpdated;
        if (firstRender) {
            // Unlike most light-DOM components, this one needs its
            // server-rendered fallback content, so it reads and clears it
            // itself instead of attaching ClearInitialContentController.
            // Reading the <pre> rather than textContent keeps the code free
            // of surrounding template whitespace.
            this._code = this.querySelector("pre")?.textContent ?? "";
            this.innerHTML = "";
        }
        super.willUpdate(changedProperties);
        if (firstRender || changedProperties.has("programmingLanguage")) {
            this._highlight();
        }
    }

    private _highlight(): void {
        const { _code: code, programmingLanguage } = this;
        if (!code) {
            this._lines = [];
            return;
        }
        // Render immediately with sync-escaped lines; upgrade when async resolves.
        this._lines = code.split("\n").map((l) => escapeHtml(l));

        const requestKey = `${programmingLanguage}::${code}`;
        this._highlightRequestKey = requestKey;
        void highlightCode(code, programmingLanguage).then((highlighted) => {
            if (this._highlightRequestKey === requestKey) {
                this._lines = highlighted.split("\n");
            }
        });
    }

    render(): TemplateResult {
        if (!this._code) return html``;

        return html`
            <div class="code-listing-container">
                <table class="code-listing highlighter-rouge">
                    <tbody>
                        ${this._lines.map(
                            (lineHtml, i) =>
                                html`<tr class="lineno">
                                    <td class="rouge-gutter gl"><pre>${i + 1}</pre></td>
                                    <td class="rouge-code"><pre>${unsafeHTML(lineHtml)}</pre></td>
                                </tr>`,
                        )}
                    </tbody>
                </table>
                <d-copy-button .text=${this._code}></d-copy-button>
            </div>
        `;
    }
}
